import { FC, useEffect, useRef, useState } from 'react';
import {
  getDownloadURL,
  UploadTask,
  UploadTaskSnapshot,
} from 'firebase/storage';

import formatPercent from 'common/formatPercent';
import Text from 'components/_core/Text';
import useToast from 'components/ToastProvider/useToast';
import { isExpectedFirebaseError } from 'features/Storage/upload';
import logger from 'helpers/logger';

import DropZone from './components/DropZone';
import { FILE_SIZE, MEGABYTE } from './fileSize';
import { composeExtensionsDisjunction, FILE_TYPE } from './fileType';
import locales from './locales';
import { PercentContainer } from './styles';
import { FileMimeType, UploadProps } from './types';
import { getMaxFileSize } from './utils';

const Upload: FC<UploadProps> = ({
  backgroundColor = 'grey.200',
  children,
  compactStyle,
  dashedBorderColor = 'grey.400',
  documentName = locales.document,
  errorMessage,
  fileTypes = FILE_TYPE.ALLOWED_DOCUMENTS,
  maxByteSizeParam: maxByteSize = FILE_SIZE.DEFAULT,
  onFileSizeExceeded = () => {},
  onSuccessfulUpload,
  // Empty functions used below as an easier to handle default value
  onUploadEnd = () => {},
  onUploadStart = () => {},
  onUploadUpdate = () => {},
  uploadFile,
  withFlatBottom,
  withLoader = true,
}) => {
  const { hideToast, showToast } = useToast();

  const uploadTaskRef = useRef<UploadTask>();
  const isMounted = useRef<boolean>(false);

  const [progress, setProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;

      // Cancel the upload on unmounting
      uploadTaskRef.current?.cancel();
    };
  }, []);

  const handleUploadTaskChange = (snapshot: UploadTaskSnapshot) => {
    uploadTaskRef.current = snapshot.task;

    const newProgress =
      Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100) / 100;

    setProgress(newProgress);
    onUploadUpdate(newProgress);
  };

  const handleUpload = async (file: File): Promise<void> => {
    const fileType = file.type as FileMimeType;

    const maxSize = getMaxFileSize({ fileType, maxByteSize });

    if (!fileTypes.includes(fileType)) {
      showToast({
        message: locales.formatString(locales.fileTypeError, {
          fileTypes: composeExtensionsDisjunction(fileTypes),
        }) as string,
        type: 'error',
      });
      return;
    }

    if (file.size > maxSize) {
      showToast({
        message: `${locales.formatString(locales.maxSizeError, {
          maxSize: maxSize / MEGABYTE,
        })}`,
        type: 'error',
      });

      onFileSizeExceeded(file.size, maxSize);

      return;
    }

    onUploadStart();
    setIsUploading(true);
    setProgress(0);
    hideToast();

    try {
      const finalSnapshot = await uploadFile(file, handleUploadTaskChange);

      setProgress(1);

      const {
        metadata: { bucket: bucketName, fullPath: fileRef },
      } = finalSnapshot;

      const url = await getDownloadURL(finalSnapshot.ref);

      if (bucketName && fileRef && url && isMounted.current) {
        await onSuccessfulUpload({
          bucketName,
          fileName: file.name,
          fileRef,
          url,
        });
      }
    } catch (error) {
      if (!isExpectedFirebaseError(error)) {
        logger.error(error);
        showToast({ message: errorMessage, type: 'error' });
      }
    } finally {
      setIsUploading(false);
      onUploadEnd();
    }
  };

  return (
    <DropZone
      accept={fileTypes.join(',')}
      backgroundColor={backgroundColor}
      compactStyle={compactStyle}
      dashedBorderColor={dashedBorderColor}
      disabled={isUploading}
      documentName={documentName}
      onUpload={handleUpload}
      withFlatBottom={withFlatBottom}
    >
      {withLoader && isUploading ? (
        <PercentContainer>
          <Text variant="primary">
            <Text as="span" fontWeight="weight-500">
              {formatPercent(progress)}
            </Text>{' '}
            {locales.uploading}
          </Text>
        </PercentContainer>
      ) : (
        children
      )}
    </DropZone>
  );
};

export default Upload;
